import { putResolve } from 'redux-saga/effects';

import { call, put, select, takeLatest } from '@redux-saga/core/effects';

import { ContextState } from '.';

import { api } from './api';
import { environment } from 'environments/environment';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import { AppAction, createActionType, createApiActionCreators, RequestActionTypes } from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import {
  ClearContextEntityType,
  ClearContextRequestActionInputs,
  ClearContextSuccessActionInputs,
} from 'models/context/ClearContext';
import { ContextCustomerEntities } from 'models/context/ContextCustomerEntities';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { selectUserLogged } from 'saga/auth/ducks';
import { redirectToLegacyCrmAction } from 'saga/crm-deeplinks/ducks';

/* STATE */

/* ACTION TYPES */
export enum ContextActionTypes {
  ClearContext = '@@Context/CLEAR_CONTEXT',
  ValidateContext = '@@Context/VALIDATE_CONTEXT',
}

/* ACTIONS */
export const clearContextAction = createApiActionCreators(ContextActionTypes.ClearContext);
export const validateContextAction = createApiActionCreators(ContextActionTypes.ValidateContext);

/* REDUCERS */

/* SELECTORS */
export const selectContext = (state: AppState) => state.context;

/**
 * Select context entities: Customer, CustomerAccount, TariffSpace, Asset
 */
export const selectContextCustomerEntities = (state: AppState): ContextCustomerEntities => {
  return {
    customer: state.context?.customer?.customer,
    customerAccount: state.context?.customerAccount?.customerAccount,
    tariffSpace: state.context?.tariffSpace?.tariffSpace,
    asset: state.context?.asset?.asset,
  };
};

/* SAGAS */
/**
 * Prepares payload DTO and calls the ClearContext.SUCCESS action.
 * @param clearContextEntity {@link ClearContextEntityType}
 * @param clearRelatedEntities true (the default) to also delete related entities from the session context
 *        Otherwise only an entity by 'clearContextEntity' will be removed.
 * @see reducers for ContextActionTypes.ClearContext + RequestActionTypes.SUCCESS
 *      and helper function {@link canClearContextEntityCheck}
 */
export function* clearContextSuccess(clearContextEntity?: ClearContextEntityType, clearRelatedEntities = true) {
  const contextState: ContextState = yield select(selectContext);
  const successActionInput: ClearContextSuccessActionInputs = {
    sessionContext: contextState,
    clearContextEntity: clearContextEntity,
    clearRelatedEntities: clearRelatedEntities,
  };

  yield putResolve(clearContextAction.success(successActionInput));
}

export function* clearContextRequest(clearContextInputs: ClearContextRequestActionInputs) {
  yield putResolve(clearContextAction.request(clearContextInputs));
}

function* clearContext({ payload }: AppAction<ClearContextRequestActionInputs>) {
  const {
    skipSharedObjectsSession,
    clearContextEntity,
    clearRelatedEntities,
    deeplinkAfterClearContext,
  }: ClearContextRequestActionInputs = payload || {};

  const isUserLogged: boolean = yield select(selectUserLogged);
  // following entities don't exist in shared objects
  const notSynchronizedEntities = [
    ClearContextEntityType.CUSTOMER_ACCOUNT,
    ClearContextEntityType.TARIFF_SPACE,
    ClearContextEntityType.ASSET,
  ];

  yield call(clearContextSuccess, clearContextEntity, !!clearRelatedEntities);

  if (
    isUserLogged &&
    !skipSharedObjectsSession &&
    (!clearContextEntity || !notSynchronizedEntities.includes(clearContextEntity))
  ) {
    const request: ApiClientRequest = {
      apiCall: call(api.deleteUserContext, yield getApiUrl(environment.urlConfig.userContext), clearContextEntity),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (!resp.ok) {
      yield put(clearContextAction.failure());

      return;
    }
  }

  if (deeplinkAfterClearContext) {
    yield put(redirectToLegacyCrmAction(deeplinkAfterClearContext));
  }
}

/* EXPORT */
export function* contextSaga() {
  yield takeLatest(createActionType(ContextActionTypes.ClearContext, RequestActionTypes.REQUEST), clearContext);
}

/**
 * Checks whether the entity (determined by 'checkEntity') should be removed from the session context.
 * @param clearContextPayload {@link ClearContextSuccessActionInputs}
 * @param checkEntity {@link ClearContextEntityType}
 * @param relatedEntities list of {@link ClearContextEntityType} (the default empty array)
 */
export const canClearContextEntityCheck = (
  clearContextPayload: ClearContextSuccessActionInputs,
  checkEntity: ClearContextEntityType,
  relatedEntities: ClearContextEntityType[] = []
): boolean =>
  !clearContextPayload.clearContextEntity ||
  clearContextPayload.clearContextEntity === checkEntity ||
  (clearContextPayload.clearRelatedEntities && relatedEntities.includes(clearContextPayload.clearContextEntity));
